import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhone } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-400 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-3 border-b border-gray-200' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <div className="flex items-center gap-3">
              <img
                src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvcG5n/9aa18ac21ab89f27a1e775c23fc1c8fe.png"
                alt="All Star Epoxy Logo"
                className="w-12 h-12 object-contain drop-shadow-md"
              />
              <span className={`font-black text-xl tracking-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                All Star <span className="text-red-600">Epoxy</span>
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors hover:text-blue-600 ${isActive ? 'text-red-600' : (isScrolled ? 'text-gray-600' : 'text-white/90')}`
                }
              >
                {link.name}
              </NavLink>
            ))}

            <a
              href="tel:9543285964"
              className={`flex items-center gap-2 text-sm font-semibold transition-colors hover:text-blue-600 ${isScrolled ? 'text-gray-600' : 'text-white/90'}`}
            >
              <SafeIcon icon={FiPhone} className="text-red-600" />
              (954) 328-5964
            </a>

            {/* Glowing Free Quote Button */}
            <Link to="/contact">
              <motion.button
                animate={{
                  boxShadow: [
                    '0 0 8px 2px rgba(220,38,38,0.4)',
                    '0 0 18px 6px rgba(220,38,38,0.7)',
                    '0 0 8px 2px rgba(220,38,38,0.4)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                whileHover={{ scale: 1.07, boxShadow: '0 0 28px 10px rgba(220,38,38,0.85)' }}
                whileTap={{ scale: 0.96 }}
                className="px-6 py-2.5 text-white text-sm font-bold rounded-full bg-red-600 hover:bg-red-700 transition-colors relative overflow-hidden"
              >
                <span className="relative z-10">Free Quote</span>
              </motion.button>
            </Link>
          </div>

          <button
            className={`md:hidden p-2 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 md:hidden"
        >
          <div className="flex flex-col px-4 py-6 gap-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `text-lg font-semibold px-4 py-3 rounded-xl ${isActive ? 'bg-red-50 text-red-600 border border-red-100' : 'text-gray-700 hover:bg-gray-50'}`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-2">
              <motion.button
                animate={{
                  boxShadow: [
                    '0 0 8px 2px rgba(220,38,38,0.4)',
                    '0 0 18px 6px rgba(220,38,38,0.7)',
                    '0 0 8px 2px rgba(220,38,38,0.4)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                whileTap={{ scale: 0.96 }}
                className="w-full px-4 py-3 text-white font-bold rounded-xl text-center bg-red-600"
              >
                Call (954) 328-5964
              </motion.button>
            </Link>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;